import {
  Button,
  Icon,
  Modal,
  SimpleDialog,
  Spinner,
  Text,
  useToastContext,
} from "@ttc3k/trekker";
import { EmptyState, PeopleTagsContextProvider } from "shared/components";
import { PeopleContextProvider } from "shared/contextProviders";
import { Box, Center } from "styled-system/jsx";
import { Grid } from "styled-system/jsx";
import { EventModalHeader } from "../EventModalHeader/EventModalHeader";
import { FormProvider } from "react-hook-form";
import { EventCreateEditModalFooter } from "../EventCreateEditModalFooter/EventCreateEditModalFooter";
import { EventPreview } from "../EventPreview/EventPreview";
import { EventDetails } from "../EventDetails/EventDetails";
import { EventHostAndLocations } from "../EventHostAndLocations/EventHostAndLocations";
import { EventSchedule } from "../EventSchedule/EventSchedule";
import { EventPhotos } from "../EventPhotos/EventPhotos";
import { useEventCreateEditForm } from "./useEventCreateEditForm";
import { useEventCreateEditContext } from "../EventCreateEditContext/useEventCreateEditContext";
import {
  lazy,
  Suspense,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { useTranslation } from "react-i18next";
import { EventByIdQuery } from "gql/generated";

const PersonDetailsModal = lazy(() =>
  import("shared/components/organisms/PersonDetailsModal").then(m => ({
    default: m.PersonDetailsModal,
  })),
);
import { getMultilingualString } from "shared/utils";
import { Xmark } from "iconoir-react";
import { usePostHog } from "shared/hooks";

export const EventCreateEditModalContent = () => {
  const { t } = useTranslation();
  const { t: tEvents } = useTranslation("events");
  const { toastFactory } = useToastContext();
  const { trackPostHogException } = usePostHog();

  // Grab the required values from the context
  const {
    defaultOrganizationId,
    defaultOrganizationTagIds,
    userEmailAddress,
    userFirstName,
    userLastName,
    event,
    isOpen,
    mode,
    isConfirmationModalOpen,
    isSavingStatusChange,
    confirmationModalMessage,
    loading,
    error,
    handleApproveClick,
    handleAcceptAttentionIssuesClick,
    handleConfirmationModalActionClick,
    handleEditClick,
    handleMenuAction,
    updateEvent,
    onClose,
    hideMenuButtons,
    currentStep,
    totalSteps,
    handleNextClick,
    handlePreviousClick,
    handleJumpToStepClick,
    isVirtual,
    userType,
    isOperatorUser,
    allLocations,
    personDetailsPersonId,
    isPersonDetailsOpen,
    onClosePersonDetails,
    setPersonDetailsPersonId,
  } = useEventCreateEditContext();

  // This is used to determine what action to take after the form is submitted
  // Either handleNextClick or handlePreviousClick
  // These are Refs because they are used in the onSubmit method of the form and
  // their values are changed just prior to form submission, so it ensures that
  // they are correct when onSuccess runs
  const actionAfterSubmit = useRef<() => void>(() => {});
  const closeOnSuccess = useRef(false);

  // This is used to determine if the form is currently being saved
  const [isSaving, setIsSaving] = useState(false);

  const {
    form,
    onSubmit,
    // error: formError,
    // defaultValues,
  } = useEventCreateEditForm({
    event,
    defaultOrganizationId,
    defaultOrganizationTagIds,
    userEmailAddress,
    userFirstName,
    userLastName,
    isOperatorUser,
    allLocations,
    onSuccess: (event: EventByIdQuery["eventById"]) => {
      setIsSaving(false);
      // Update the event in useEventCreateEditModal AND useEvents
      updateEvent(event);
      // Reset the dirty states on the form with the current values
      // We do this because we save with each "next"/"prev" click
      // and we don't want to retrigger the same save action
      form.reset(form.getValues());
      // Show a toast to the user to let them know the event was updated
      toastFactory.create({
        title: t("core:SUCCESS"),
        description: t("events:APP.MODAL.MUTATION_RESULT.SUCCESS", {
          title: getMultilingualString(event?.title ?? {}),
        }),
        duration: 3000,
      });
      // This will usually either be handleNextClick or handlePreviousClick
      if (typeof actionAfterSubmit.current === "function") {
        actionAfterSubmit.current();
      }
      if (closeOnSuccess.current) {
        onClose();
      }
    },
    onError: () => {
      setIsSaving(false);
      trackPostHogException(new Error("Error saving event"), {
        fileName: "EventCreateEditModalContent.tsx",
        method: "onError",
        message: "Error saving event",
      });
      // Show a toast to the user to let them know there was an error
      toastFactory.create({
        title: t("core:ERROR.TITLE"),
        description: t("events:APP.MODAL.MUTATION_RESULT.ERROR", {
          title: getMultilingualString(event?.title ?? {}),
        }),
        duration: 3000,
      });
    },
  });

  // When the currentStep changes, we need to update the form's step value
  // This is used in the superRefine, so we can run validation only when on specific steps
  useEffect(() => {
    form.setValue("step", currentStep, { shouldDirty: false });
  }, [currentStep, form]);

  // When the modal is closed, we need to reset the form
  // The ensures the UI does not show error states when the
  // modal is reopened
  useEffect(() => {
    if (!isOpen) {
      closeOnSuccess.current = false;
      setIsSaving(false);
      form.reset();
    } else {
      // There is no UI for this form control, so we need to register it
      // so that it can be reset correctly
      form.register("imagesToKeep");
    }
  }, [form, isOpen]);

  // When clicking next/prev, we need to trigger the form validation and possibly
  // save the current values
  // and then call the onSuccessAction callback
  const triggerForm = async (
    onSuccessAction: (step: number) => void,
    isSubmitting = false,
    step: number = 0,
  ) => {
    // Always set isSubmitting explicitly (true AND false), with shouldDirty
    // always true so React Hook Form recomputes dirtiness against the
    // (false) default every time. Otherwise a previous failed-validation
    // submit attempt could leave isSubmitting stuck dirty+true in form
    // state and leak into a later non-submit Save/Next action.
    form.setValue("isSubmitting", isSubmitting, { shouldDirty: true });
    const success = await form.trigger();
    if (success) {
      // A valid final submit always issues the mutation, even if no other
      // field is dirty (isSubmitting alone must reach the server).
      if (isSubmitting || Object.keys(form.formState.dirtyFields).length > 0) {
        // We need to wrap the onSuccessAction in an anonymous function
        // so that it isn't called immediately
        actionAfterSubmit.current = () => onSuccessAction(step);
        closeOnSuccess.current = isSubmitting;
        setIsSaving(true);
        onSubmit();
      } else {
        onSuccessAction(step);
      }
    }
  };

  const getModalContent = () => {
    if (mode === "approve" || mode === "view") return <EventPreview />;

    switch (currentStep) {
      case 1:
        return <EventDetails />;
      case 2:
        return <EventHostAndLocations />;
      case 3:
        return <EventPhotos />;
      case 4:
        return <EventSchedule />;
      case 5:
        return <EventPreview />;
    }
  };

  // This avoids <form> in <form> errors when modals are stacked and not portalled
  const FormWrapper = mode !== "view" ? "form" : "div";

  /**
   * Block the browser's implicit "Enter submits the form" behaviour. This is a
   * multi-step wizard whose saving/submitting is driven entirely by the footer
   * buttons, so an implicit submit is always accidental — e.g. hitting Enter
   * while the venue search is still loading (no option to select yet) would
   * otherwise submit the whole event with a half-filled location.
   *
   * Runs in the bubble phase, so a combobox/listbox that handles Enter itself
   * has already done so; preventDefault only cancels the form submission.
   * Textareas keep Enter for newlines, and explicit submit buttons still work.
   */
  const handleFormKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== "Enter" || event.shiftKey) return;

    const target = event.target as HTMLElement | null;
    if (!target) return;

    const tag = target.tagName.toLowerCase();
    const isSubmitControl =
      (tag === "button" && (target as HTMLButtonElement).type === "submit") ||
      target.getAttribute("type") === "submit";

    if (tag === "textarea" || isSubmitControl) return;

    event.preventDefault();
  };

  return (
    <>
      <Modal
        open={isOpen}
        placement={"rightLargePane"}
        onOpenChange={onClose}
        closeTriggerVisual={"subtle"}
        closeTriggerPlacement="whereabouts"
        portal
        closeTriggerProps={{
          bgColor: "ink.1000/75",
          backdropFilter: "blur(4px)",
          borderRadius: "full",
          top: "32px",
          right: { base: "16px", md: "32px" },
          _hover: {
            bgColor: "ink.1000/95",
            "&[disabled]": {
              bgColor: "ink.1000/75",
            },
          },
          _focusVisible: {
            bgColor: "ink.1000/95",
            "&[disabled]": {
              bgColor: "ink.1000/75",
            },
          },
          style: {
            transition: "background-color 0.2s ease-in-out",
          },
          children: (
            <Icon Element={Xmark} w="16px" h="16px" color="icon.inverse" />
          ),
        }}
        noMaxH
        userSelect="none"
        closeOnInteractOutside={false}
      >
        <Grid
          gridTemplateRows={mode === "view" ? "1fr" : "1fr 88px"}
          columnGap={0}
          gridColumnGap={0}
          gap={0}
          w={{ base: "100%", md: "520px" }}
          h="full"
          bgColor="#FFF"
        >
          {loading ? (
            <Center h="full">
              <Spinner w="32px" h="32px" />
            </Center>
          ) : error ? (
            <EmptyState text="Error loading event" p="500" h="full" />
          ) : (
            <Box
              py={mode === "approve" || mode === "view" ? "0" : "500"}
              px={{
                base: mode === "approve" || mode === "view" ? "0" : "300",
                md: mode === "approve" || mode === "view" ? "0" : "500",
              }}
              overflowY="auto"
            >
              <EventModalHeader event={event} />
              {!!event?.attentionIssues?.length &&
                mode !== "view" &&
                mode !== "approve" && (
                  <Box
                    bgColor="bg.warning.light"
                    borderRadius="100"
                    mb="250"
                    p="200"
                  >
                    <Text visual="smallSemiBold" color="text.warning">
                      {tEvents("APP.HEADER.VIEWS.NEEDS_ATTENTION.TITLE")}
                    </Text>
                    {event.attentionIssues.filter(Boolean).map(issue => (
                      <Text
                        key={`${issue?.type}-${issue?.venueIndex ?? ""}`}
                        visual="smallRegular"
                        color="text.dark"
                      >
                        {tEvents(`APP.CARD.ATTENTION.${issue?.type}`, {
                          name: issue?.sourceName ?? "",
                        })}
                      </Text>
                    ))}
                    <Box mt="150">
                      <Button
                        type="button"
                        visual="warning"
                        mode="light"
                        size="sm"
                        isLoading={isSavingStatusChange}
                        disabled={isSavingStatusChange}
                        onClick={handleAcceptAttentionIssuesClick}
                      >
                        {tEvents("APP.CARD.ATTENTION.ACCEPT_AS_IS")}
                      </Button>
                    </Box>
                  </Box>
                )}
              <FormProvider {...form}>
                <FormWrapper
                  id="event-create-edit-form"
                  onSubmit={onSubmit}
                  onKeyDown={handleFormKeyDown}
                >
                  {getModalContent()}
                </FormWrapper>
              </FormProvider>
            </Box>
          )}
          {mode !== "view" && (
            <EventCreateEditModalFooter
              isSaving={isSaving || isSavingStatusChange}
              userType={userType}
              currentStep={currentStep}
              totalSteps={totalSteps}
              hasDirtyFields={
                Object.keys(form.formState.dirtyFields).length > 0
              }
              formTriggerFn={form.trigger}
              handleApproveClick={handleApproveClick}
              handleDeclineClick={() => handleMenuAction({ value: "decline" })}
              handleEditClick={handleEditClick}
              handleMenuAction={handleMenuAction}
              handleNextClick={() => triggerForm(handleNextClick)}
              handlePreviousClick={() => triggerForm(handlePreviousClick)}
              handleJumpToStepClick={(step: number) =>
                triggerForm(handleJumpToStepClick, false, step)
              }
              handleSubmitClick={() => triggerForm(() => {}, true)}
              handleSaveClick={() => triggerForm(() => {}, false)}
              isVirtual={isVirtual}
              hideMenuButtons={hideMenuButtons}
            />
          )}
        </Grid>
      </Modal>
      <SimpleDialog
        open={isConfirmationModalOpen}
        title={t("core:CONFIRM.TITLE")}
        message={confirmationModalMessage}
        cancelButtonCopy={t("core:CONFIRM.NO")}
        onCancelClick={() => handleConfirmationModalActionClick(false)}
        confirmButtonCopy={t("core:CONFIRM.YES")}
        confirmButtonVisual="destructive"
        onConfirmClick={() => handleConfirmationModalActionClick(true)}
        size="md"
        portal
      />
      {isPersonDetailsOpen && personDetailsPersonId && (
        <PeopleContextProvider NOP>
          <PeopleTagsContextProvider>
            <Suspense fallback={null}>
              <PersonDetailsModal
                isOpen={isPersonDetailsOpen}
                setIsOpen={v => {
                  const open =
                    typeof v === "function" ? v(isPersonDetailsOpen) : v;
                  if (!open) onClosePersonDetails();
                }}
                personId={personDetailsPersonId}
                setPersonId={value =>
                  setPersonDetailsPersonId(
                    typeof value === "function"
                      ? value(personDetailsPersonId)
                      : value,
                  )
                }
                portal
              />
            </Suspense>
          </PeopleTagsContextProvider>
        </PeopleContextProvider>
      )}
    </>
  );
};
